/**
 * The colouring the published connection snippets are drawn with.
 *
 * The published page runs CodeMirror in its `javascript` mode over every tab:
 * the Python, Go and Java samples included, because the embed hard-codes
 * `code-mode="javascript"`, and paints it with a Dracula theme. So this is a
 * javascript-mode tokeniser rather than a general highlighter: it exists to put
 * the same categories on the same characters. `highlight.js`, which the blog
 * uses, leaves plain identifiers unmarked where CodeMirror paints every one of
 * them, which is most of the colour on the panel.
 *
 * The eight colours are measured off the published page, not copied from a
 * theme file.
 */

type CodeToken = {
  text: string;
  kind:
    | "plain"
    | "keyword"
    | "atom"
    | "number"
    | "string"
    | "comment"
    | "property"
    | "variable";
};

/** Measured with `getComputedStyle` on the published `.CodeMirror-code`. */
export const CODE_COLORS: Record<CodeToken["kind"], string> = {
  plain: "#ECECEC",
  keyword: "#ECECEC",
  atom: "#F99393",
  number: "#F99393",
  string: "#F1FA8C",
  comment: "#605C7B",
  property: "#FF79C6",
  variable: "#B290FC",
};

export const CODE_GROUND = "#041E39";
export const CODE_GUTTER_INK = "#605C7B";

const KEYWORDS = new Set([
  "async",
  "await",
  "break",
  "case",
  "catch",
  "class",
  "const",
  "continue",
  "debugger",
  "default",
  "delete",
  "do",
  "else",
  "export",
  "extends",
  "finally",
  "for",
  "from",
  "function",
  "if",
  "import",
  "in",
  "instanceof",
  "let",
  "new",
  "of",
  "return",
  "static",
  "super",
  "switch",
  "this",
  "throw",
  "try",
  "typeof",
  "var",
  "void",
  "while",
  "yield",
]);

const ATOMS = new Set([
  "true",
  "false",
  "null",
  "undefined",
  "NaN",
  "Infinity",
]);

const WORD = /[A-Za-z_$][\w$]*/y;
const NUMBER = /(?:0[xX][\da-fA-F]+|\d[\d._]*(?:[eE][+-]?\d+)?)/y;
const LINE_COMMENT = /\/\/[^\n]*/y;

type Slice = { text: string; kind: CodeToken["kind"] };

function commentAt(line: string, index: number): Slice | null {
  LINE_COMMENT.lastIndex = index;
  const comment = LINE_COMMENT.exec(line);
  return comment ? { text: comment[0], kind: "comment" } : null;
}

/** Where the string opened at `index` closes, past its escapes; the line's end if it never does. */
function stringEnd(line: string, index: number): number {
  const quote = line[index];
  let end = index + 1;
  while (end < line.length) {
    if (line[end] === "\\") {
      end += 2;
      continue;
    }
    end += 1;
    if (line[end - 1] === quote) break;
  }
  return end;
}

function stringAt(line: string, index: number): Slice | null {
  const quote = line[index];
  if (quote !== '"' && quote !== "'" && quote !== "`") return null;
  return { text: line.slice(index, stringEnd(line, index)), kind: "string" };
}

function numberAt(line: string, index: number): Slice | null {
  if (!/\d/.test(line.charAt(index))) return null;
  NUMBER.lastIndex = index;
  const number = NUMBER.exec(line);
  return number ? { text: number[0], kind: "number" } : null;
}

/**
 * A name is a property only where a dot put it there; CodeMirror's javascript
 * mode decides the same way, and it is the one category that depends on what
 * came before rather than on the characters themselves.
 */
function wordKind(value: string, previous: string): CodeToken["kind"] {
  if (KEYWORDS.has(value)) return "keyword";
  if (ATOMS.has(value)) return "atom";
  return previous === "." ? "property" : "variable";
}

function wordAt(line: string, index: number, previous: string): Slice | null {
  WORD.lastIndex = index;
  const word = WORD.exec(line);
  return word ? { text: word[0], kind: wordKind(word[0], previous) } : null;
}

/** Adjacent slices of one kind merge into one span, as the published panel draws them. */
function append(tokens: Array<CodeToken>, { text, kind }: Slice) {
  const last = tokens[tokens.length - 1];
  if (last && last.kind === kind) last.text += text;
  else tokens.push({ text, kind });
}

/**
 * One line, as the spans the published panel draws over it. Line by line rather
 * than over the whole sample because the panel is a list of lines and a token
 * never crosses one in these samples.
 */
export function tokenizeLine(line: string): ReadonlyArray<CodeToken> {
  const tokens: Array<CodeToken> = [];
  let index = 0;
  /** The last glyph that was not whitespace, which decides whether a name is a property. */
  let previous = "";
  while (index < line.length) {
    const slice = commentAt(line, index) ??
      stringAt(line, index) ??
      numberAt(line, index) ??
      wordAt(line, index, previous) ?? {
        text: line.charAt(index),
        kind: "plain",
      };
    append(tokens, slice);
    const glyphs = slice.text.trim();
    if (glyphs) previous = glyphs.slice(-1);
    index += slice.text.length;
  }
  return tokens;
}
